import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

interface ProductTaxItem {
  product_id: number;
  prod_name: string;
  tax1_name: string;
  tax1_per: string;
  currency: string;
}

interface ListProductTaxResponse {
  success: boolean;
  count: number;
  product_tax_list: ProductTaxItem[];
  message?: string;
}

interface ProductTaxResponse {
  success: boolean;
  message: string;
  tax1_name?: string;
  tax1_per?: string | number;
  currency?: string;
  product_id?: number;
  tax_name?: string;
  tax_per?: string | number;
}

interface SetProductTaxRequest {
  product_id?: number;
  tax_name: string;
  tax_per: string;
  currency: string;
}

interface SetProductTaxResponse {
  success: boolean;
  message: string;
  tax1_name?: string;
  tax1_per?: string | number;
  currency?: string;
  product_id?: number;
}

interface ProductReturnLimitResponse {
  success: boolean;
  message: string;
  return_limit?: string | number;
  product_id?: number;
  global_return_limit?: number;
  product_limits?: any[];
  source?: string;
}

interface SetProductReturnLimitRequest {
  product_id?: number;
  return_limit: string;
}

interface SetProductReturnLimitResponse {
  success: boolean;
  message: string;
  return_limit?: string | number;
  product_id?: number;
}

export const globalSettingsApi = createApi({
  reducerPath: 'globalSettingsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_ENDPOINT || 'https://fftestservice.sarsetech.com',
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('vendor_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      headers.set('Content-Type', 'application/x-www-form-urlencoded');
      return headers;
    },
  }),
  tagTypes: ['GlobalSettings', 'ProductTax', 'ProductReturnLimit', 'ProductTaxList'],
  endpoints: (build) => ({
    // Get all product taxes list
    listAllProductTaxes: build.query<ListProductTaxResponse, void>({
      query: () => ({
        url: 'globalsettings/listProductTax',
        method: 'POST',
        body: new URLSearchParams().toString(),
      }),
      providesTags: ['ProductTaxList'],
      transformResponse: (response: any) => {
        console.log('List All Product Taxes Response:', response);
        
        if (response && response.success) {
          return {
            success: response.success,
            count: response.count || 0,
            product_tax_list: Array.isArray(response.product_tax_list) 
              ? response.product_tax_list.map((item: any) => ({
                  product_id: item.product_id || 0,
                  proj_name: item.prod_name || item.proj_name || '',
                  tax_name: item.tax1_name || '',
                  tax_per: String(item.tax1_per || ''),
                  currency: item.currency || 'AED'
                }))
              : []
          };
        }
        
        return {
          success: false,
          count: 0,
          product_tax_list: []
        };
      },
    }),

    // Get global product tax
    getProductTax: build.query<ProductTaxResponse, void>({
      query: () => ({
        url: 'globalsettings/setProductTax',
        method: 'POST',
        body: new URLSearchParams().toString(),
      }),
      providesTags: ['GlobalSettings'],
      transformResponse: (response: any) => {
        console.log('Get Global Product Tax Response:', response);
        
        if (response && response.success) {
          return {
            success: response.success,
            message: response.message || 'Success',
            tax_name: response.tax1_name || 'VAT',
            tax_per: String(response.tax1_per || ''),
            currency: response.currency || 'AED',
            tax1_name: response.tax1_name,
            tax1_per: response.tax1_per
          };
        }
        
        return {
          success: false,
          message: response?.message || 'Failed to load tax settings'
        };
      },
    }),
    
    // Get product-specific tax
    getProductTaxById: build.query<ProductTaxResponse, { product_id: number }>({
      query: ({ product_id }) => ({
        url: 'globalsettings/listProductTax',
        method: 'POST',
        body: new URLSearchParams({
          product_id: product_id.toString()
        }).toString(),
      }),
      providesTags: (result, error, { product_id }) => [
        { type: 'ProductTax', id: product_id }
      ],
      transformResponse: (response: any) => {
        console.log('Get Product Tax by ID Response:', response);
        
        if (response && response.success && response.product_tax_list && response.product_tax_list.length > 0) {
          const item = response.product_tax_list[0];
          return {
            success: true,
            message: 'Success',
            tax_name: item.tax1_name || '',
            tax_per: String(item.tax1_per || ''),
            currency: item.currency || 'AED',
            product_id: item.product_id
          };
        }
        
        return {
          success: false,
          message: 'Product tax not found'
        };
      },
    }),
    
    // Set product tax
    setProductTax: build.mutation<SetProductTaxResponse, SetProductTaxRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('tax_name', data.tax_name);
        formData.append('tax_per', data.tax_per);
        formData.append('currency', data.currency);
        
        if (data.product_id) {
          formData.append('product_id', data.product_id.toString());
        }
        
        console.log('Set Product Tax Request:', data);
        
        return {
          url: 'globalsettings/setProductTax',
          method: 'POST',
          body: formData.toString(),
        };
      },
      invalidatesTags: (result, error, { product_id }) => [
        { type: 'GlobalSettings' },
        { type: 'ProductTax', id: product_id },
        { type: 'ProductTaxList' }
      ],
      transformResponse: (response: any) => {
        console.log('Set Product Tax Response:', response);
        
        if (response && response.success) {
          return {
            success: response.success,
            message: response.message || 'Tax settings updated successfully',
            tax1_name: response.tax1_name,
            tax1_per: response.tax1_per,
            currency: response.currency,
            product_id: response.product_id
          };
        }
        
        return {
          success: false,
          message: response?.message || 'Failed to update tax settings'
        };
      },
      transformErrorResponse: (response: any) => {
        return {
          success: false,
          message: response?.data?.message || 'Network error'
        };
      }
    }),
    
    // Get global product return limit
    getProductReturnLimit: build.query<ProductReturnLimitResponse, void>({
      query: () => ({
        url: 'globalsettings/productReturnLimit',
        method: 'POST',
        body: new URLSearchParams().toString(),
      }),
      providesTags: ['GlobalSettings'],
      transformResponse: (response: any) => {
        console.log('Get Global Product Return Limit Response:', response);
        
        if (response && response.success) {
          const returnLimit = response.global_return_limit || response.return_limit;
          
          return {
            success: response.success,
            message: response.message || 'Success',
            return_limit: returnLimit !== undefined ? String(returnLimit) : '3',
            global_return_limit: response.global_return_limit,
            product_limits: response.product_limits,
            source: response.source
          };
        }
        
        return {
          success: false,
          message: response?.message || 'Failed to load return limit'
        };
      },
    }),
    
    // Get product-specific return limit
    getProductReturnLimitById: build.query<ProductReturnLimitResponse, { product_id: number }>({
      query: ({ product_id }) => ({
        url: 'globalsettings/productReturnLimit',
        method: 'POST',
        body: new URLSearchParams({
          product_id: product_id.toString()
        }).toString(),
      }),
      providesTags: (result, error, { product_id }) => [
        { type: 'ProductReturnLimit', id: product_id }
      ],
      transformResponse: (response: any) => {
        console.log('Get Product Return Limit by ID Response:', response);
        
        if (response && response.success) {
          return {
            success: response.success,
            message: response.message || 'Success',
            return_limit: String(response.return_limit || '0'),
            product_id: response.product_id,
            source: response.source
          };
        }
        
        return {
          success: false,
          message: 'Return limit not found'
        };
      },
    }),
    
    // Set product return limit
    setProductReturnLimit: build.mutation<SetProductReturnLimitResponse, SetProductReturnLimitRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('return_limit', data.return_limit);
        
        if (data.product_id) {
          formData.append('product_id', data.product_id.toString());
        }
        
        console.log('Set Product Return Limit Request:', data);
        
        return {
          url: 'globalsettings/setProductReturnLimit',
          method: 'POST',
          body: formData.toString(),
        };
      },
      invalidatesTags: (result, error, { product_id }) => [
        { type: 'GlobalSettings' },
        { type: 'ProductReturnLimit', id: product_id }
      ],
      transformResponse: (response: any) => {
        console.log('Set Product Return Limit Response:', response);
        
        if (response && response.success) {
          return {
            success: response.success,
            message: response.message || 'Return limit updated successfully',
            return_limit: response.return_limit,
            product_id: response.product_id
          };
        }
        
        return {
          success: false,
          message: response?.message || 'Failed to update return limit'
        };
      },
      transformErrorResponse: (response: any) => {
        return {
          success: false,
          message: response?.data?.message || 'Network error'
        };
      }
    }),
  }),
});

export const {
  useListAllProductTaxesQuery,
  useGetProductTaxQuery,
  useGetProductTaxByIdQuery,
  useSetProductTaxMutation,
  useGetProductReturnLimitQuery,
  useGetProductReturnLimitByIdQuery,
  useSetProductReturnLimitMutation,
} = globalSettingsApi;